import * as Select from '@radix-ui/react-select';
import { Trans, useTranslation } from 'react-i18next';

import { DropdownItems } from '~/components/DropdownTreeSingleSelect/components/DropdownItems';
import { DropdownNoOptionsAvailable } from '~/components/DropdownTreeSingleSelect/components/DropdownNoOptionsAvailable';
import { DropdownSearch } from '~/components/DropdownTreeSingleSelect/components/DropdownSearch';
import { Scrollbars } from '~/components/DropdownTreeSingleSelect/components/Scrollbars';
import type {
  DropdownSearchOptions,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownMenuProps extends RadixUiProps {
  options: InternalDropdownOption[];
  searchOptions?: DropdownSearchOptions;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedOption?: InternalDropdownOption;
  onSelectOption: (option: InternalDropdownOption) => void;
  onExpandOption: (option: InternalDropdownOption, isExpanded: boolean) => void;
  showCheckIcon?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownMenu({
  options,
  searchOptions,
  searchQuery,
  onSearchQueryChange,
  selectedOption,
  onSelectOption,
  onExpandOption,
  showCheckIcon,
  OptionTemplate,
  radix = {},
}: DropdownMenuProps) {
  const { t } = useTranslation('common');
  const isOneOrMoreOptionsVisible = options.some((option) => option.isVisible);
  const searchQueryTrimmed = searchQuery.trim();

  return (
    <Select.Portal>
      <Select.Content
        // Prevents the dropdown menu from overlapping the dropdown trigger.
        // https://www.radix-ui.com/primitives/docs/components/select#content
        position="popper"
        {...radix.selectContent}
        className={cn(
          'mt-xs min-w-[var(--radix-popper-anchor-width)] overflow-auto text-body-sm text-default',
          'rounded-xs border shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
          radix.selectContent?.className,
        )}
        style={
          {
            // This is used to calculate the scroll height of the dropdown menu.
            '--dropdown-menu-search-height': searchOptions?.isEnabled
              ? '78px'
              : '0',
          } as React.CSSProperties
        }
      >
        {!searchQuery && !options?.length ? (
          <DropdownNoOptionsAvailable radix={radix} />
        ) : (
          <>
            {searchOptions?.isEnabled ? (
              <DropdownSearch
                value={searchQuery}
                onChange={onSearchQueryChange}
              />
            ) : null}
            <Select.Viewport {...radix.selectViewport}>
              <Scrollbars
                radix={{
                  ...radix,
                  scrollAreaViewport: {
                    className: cn(
                      'max-h-[min(calc(var(--radix-popper-available-height)-var(--dropdown-menu-search-height)),600px)]',
                      radix.scrollAreaViewport?.className,
                    ),
                    ...radix.scrollAreaViewport,
                  },
                }}
              >
                {isOneOrMoreOptionsVisible ? (
                  <DropdownItems
                    depth={0}
                    options={options}
                    searchQuery={searchQueryTrimmed}
                    selectedOption={selectedOption}
                    onSelectOption={onSelectOption}
                    onExpandOption={onExpandOption}
                    showCheckIcon={showCheckIcon}
                    OptionTemplate={OptionTemplate}
                    radix={radix}
                  />
                ) : (
                  <DropdownNoOptionsAvailable
                    labelText={t('dropdownTree.noMatchingOptionsText', {
                      searchQuery: searchQueryTrimmed,
                    })}
                    labelNode={
                      <span className="text-emphasis-stronger">
                        <Trans
                          i18nKey="dropdownTree.noMatchingOptionsHtml"
                          t={t}
                          values={{
                            searchQuery: searchQueryTrimmed,
                          }}
                          components={{
                            strong: (
                              <span className="font-semibold text-default" />
                            ),
                          }}
                        />
                      </span>
                    }
                    radix={radix}
                  />
                )}
              </Scrollbars>
            </Select.Viewport>
          </>
        )}
      </Select.Content>
    </Select.Portal>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
